import { keyBy } from 'lodash-es'
import { API } from '#app/lib/api/api'
import preq from '#app/lib/preq'
import type { ItemsPage } from '#server/controllers/items/lib/queries_commons'
import type { PageParams } from '#server/lib/pagination'
import type { EntityUri } from '#server/types/entity'
import type { Item, ItemId } from '#server/types/item'
import type { UserId } from '#server/types/user'
import { getRelations } from '#users/lib/relations'
import { serializeUser, type SerializedUser } from '#users/lib/users'
import { serializeItem, setItemUserData, type SerializedItemWithUserData } from './items'
import type { OverrideProperties } from 'type-fest'

export type SerializedItemsPage = OverrideProperties<ItemsPage, { items: SerializedItemWithUserData[], users: SerializedUser[] }>

export async function getItemsByUserIdAndEntities (userId: UserId, uris: EntityUri | EntityUri[]) {
  const { items, users } = await preq.get(API.items.byUserAndEntities(userId, uris, true))
  const serializedUsers = users.map(serializeUser)
  const serializedItems = items.map(serializeItem)
  const usersByIds = keyBy(serializedUsers, '_id')
  return serializedItems.map(item => {
    return setItemUserData(item, usersByIds[item.owner])
  }) as SerializedItemWithUserData[]
}

async function makeRequestAlt (params, endpoint, ids, filter?) {
  if (ids.length === 0) return { items: [] as Item[], total: 0 } as ItemsPage
  const { limit, offset } = params
  const res = await preq.get(API.items[endpoint]({ ids, limit, offset, filter, includeUsers: true }))
  updateItemsParams(res, params)
  return res as SerializedItemsPage
}

export async function getItemsByIds ({ ids, items }) {
  const res = await preq.get(API.items.byIds({ ids, includeUsers: true }))
  updateItemsParams(res, { items })
  return res as SerializedItemsPage
}

export async function getSerializedItemWithUser (itemId: ItemId) {
  const res = await preq.get(API.items.byIds({ ids: [ itemId ], includeUsers: true }))
  serializeItemsWithUsers(res)
  return res.items[0] as SerializedItemWithUserData
}

export async function getNearbyItems (params) {
  const { limit, offset } = params
  const res = await preq.get(API.items.nearby(limit, offset))
  updateItemsParams(res, params)
  return res as SerializedItemsPage
}

export async function getRecentPublicItems (params) {
  const { limit, lang, assertImage } = params
  const res = await preq.get(API.items.recentPublic(limit, lang, assertImage))
  updateItemsParams(res, params)
  return res as SerializedItemsPage
}

export async function getItemsByBbox (params) {
  const { bbox, limit, lang } = params
  const res = await preq.get(API.items.byBbox(bbox, limit, lang))
  updateItemsParams(res, params)
  return res as SerializedItemsPage
}

export async function getUserItems (params) {
  const { userId } = params
  return makeRequestAlt(params, 'byUsers', [ userId ])
}

export async function getNetworkItems (params) {
  const { network: networkIds } = await getRelations()
  return makeRequestAlt(params, 'byUsers', networkIds)
}

interface ItemsPageParams extends Partial<PageParams> {
  hasMore?: boolean
  items: SerializedItemWithUserData[]
}

function updateItemsParams (res: ItemsPage, params: ItemsPageParams) {
  const { items: newItems, continue: continu, total } = res
  params.hasMore = continu != null
  params.total = total
  serializeItemsWithUsers(res)
  params.items.push(...(newItems as SerializedItemWithUserData[]))
  return res as SerializedItemsPage
}

export function serializeItemsWithUsers (res: ItemsPage) {
  const { items, users } = res
  const usersById = keyBy(users.map(serializeUser), '_id')
  res.items = items.map(serializeItem)
  items.forEach(item => {
    // @ts-expect-error
    item.user = usersById[item.owner]
  })
  return res as SerializedItemsPage
}

export type ItemsPageRequestFn = typeof getNetworkItems | typeof getNearbyItems
